import { RoleType } from '@/features/admin/roles/roleTypes'
import { UserType } from '@/features/admin/users/userTypes'
import { type SectionConfig } from '@/utils/get-matched-section'

// Admin is a super-admin-only app (gated by `userHasSystemAdmin` on the
// `(super)` layout). It has no `orgSlug` prefix, so every key here is the
// literal `/admin/...` path. Two shapes:
//   - System scope: app-wide lists + utilities (organizations, users, roles…)
//   - Organization drill-in: `/admin/organizations/[organizationId]/…`, the
//     selected org's management surfaces as first-class routes.
// Detail records (member/group/user/role) use a Link-based `RecordSubNav`
// instead of `?tab=` search params, so there are no `tabs` arrays here.
export const sectionConfig: SectionConfig = {
  '/admin': {
    title: 'Home',
    description: 'Manage organizations, users, and roles across the platform.',
    showBreadcrumbs: false
  },

  // ── Organizations ─────────────────────────────────────────────────────────
  // The org list lives on the Home landing (drill-in grid); there is no
  // standalone `/admin/organizations` page.
  '/admin/organizations/new': {
    title: 'New Customer Organization',
    showBreadcrumbs: false
  },
  '/admin/organizations/new-technician': {
    title: 'New Technician Organization',
    showBreadcrumbs: false
  },

  // ── Organization drill-in ─────────────────────────────────────────────────
  // The selected org's name is surfaced by the sidebar's Organization scope
  // block, so these pages keep terse section titles.
  '/admin/organizations/[organizationId]/overview': {
    title: 'Overview',
    showBreadcrumbs: false
  },
  '/admin/organizations/[organizationId]/members': {
    title: 'Members',
    showBreadcrumbs: false
  },
  '/admin/organizations/[organizationId]/members/[memberId]': {
    title: 'Member',
    showBreadcrumbs: false
  },
  '/admin/organizations/[organizationId]/groups': {
    title: 'Groups',
    showBreadcrumbs: false
  },
  '/admin/organizations/[organizationId]/groups/[groupId]': {
    title: 'Group',
    showBreadcrumbs: false
  },
  '/admin/organizations/[organizationId]/groups/[groupId]/members': {
    title: 'Group Members',
    showBreadcrumbs: false
  },
  '/admin/organizations/[organizationId]/groups/[groupId]/properties': {
    title: 'Group Properties',
    showBreadcrumbs: false
  },
  '/admin/organizations/[organizationId]/roles': {
    title: 'Roles',
    showBreadcrumbs: false
  },
  '/admin/organizations/[organizationId]/properties': {
    title: 'Properties',
    showBreadcrumbs: false
  },
  '/admin/organizations/[organizationId]/invitations': {
    title: 'Invitations',
    showBreadcrumbs: false
  },
  '/admin/organizations/[organizationId]/sso': {
    title: 'Single Sign-On (SSO)',
    showBreadcrumbs: false
  },
  '/admin/organizations/[organizationId]/settings': {
    title: 'Organization Settings',
    showBreadcrumbs: false
  },

  // ── System scope: Users ───────────────────────────────────────────────────
  '/admin/users': {
    title: 'Users',
    description: 'View and manage users.',
    showBreadcrumbs: false
  },
  '/admin/users/[id]': {
    title: 'Edit User',
    breadcrumb: (_params, user: unknown) => (user as UserType)?.name || 'User',
    parentBreadcrumb: {
      label: 'Users',
      // Super-admin route — no orgSlug prefix.
      href: () => '/admin/users'
    },
    showBreadcrumbs: true,
    showContext: true,
    contextLabel: 'System User',
    contextValue: (_params: unknown, user?: unknown) =>
      (user as UserType)?.name || 'Unknown User'
  },

  // ── System scope: Audit Logs ──────────────────────────────────────────────
  '/admin/audit-logs': {
    title: 'Audit Logs',
    description:
      'Who did what, when, and from where — every mutation, denied attempt, and auth event across the platform.',
    showBreadcrumbs: false
  },

  // ── System scope: User Activity ────────────────────────────────────────────
  // No `fullBleed`: Admin's other table page (audit-logs) doesn't use it, and
  // the section layout's own scroll is what both tables here expect.
  '/admin/activity': {
    title: 'User Activity',
    description:
      'Where internal staff are in Atlas right now, and which pages the day actually went to.',
    showBreadcrumbs: false
  },

  // ── System scope: Roles ───────────────────────────────────────────────────
  '/admin/roles': {
    title: 'Roles',
    description: 'Manage roles and permission assignments.',
    showBreadcrumbs: false
  },
  '/admin/roles/new': {
    title: 'New Role',
    showBreadcrumbs: false
  },
  '/admin/roles/[id]': {
    title: 'Edit Role',
    breadcrumb: (_params, role: unknown) => (role as RoleType)?.name || 'Role',
    parentBreadcrumb: {
      label: 'Roles',
      href: () => '/admin/roles'
    },
    showBreadcrumbs: true,
    showContext: true,
    contextLabel: 'Editing Role',
    contextValue: (_params: unknown, role?: unknown) =>
      (role as RoleType)?.name || 'Unknown Role'
  },

  // ── System scope: Utilities ───────────────────────────────────────────────
  '/admin/eol-import': {
    title: 'EOL Import',
    description:
      'Upload the HPE Aruba end-of-life CSV to refresh device EOL data.',
    showBreadcrumbs: false
  }
}
